import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Mic, Plus, Play, Pause, Upload, Download, ChevronDown, ChevronUp, Trash2, FileText, Zap, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceProfileManager = ({ seriesFilter = "the-red-ink-chronicles" }) => {
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [voiceForm, setVoiceForm] = useState({ character_name: '', series: seriesFilter, voice_type: 'ai', ai_voice: 'onyx', ai_speed: 1.0, description: '', is_narrator: false });
  const [editingId, setEditingId] = useState(null);
  const [generatingSample, setGeneratingSample] = useState(null);
  const [playingSample, setPlayingSample] = useState(null);
  const sampleAudioRef = useRef(null);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/voice-profiles?series=${seriesFilter}`);
      setVoiceProfiles(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchProfiles(); }, [seriesFilter]);

  useEffect(() => {
    setVoiceForm(prev => ({ ...prev, series: seriesFilter }));
  }, [seriesFilter]);

  const saveProfile = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/voice-profiles/${editingId}`, voiceForm);
      } else {
        await axios.post(`${API}/voice-profiles`, voiceForm);
      }
      fetchProfiles();
      setShowForm(false);
      setEditingId(null);
    } catch (err) { console.error(err); alert('Failed to save voice profile'); }
  };

  const deleteProfile = async (id, name) => {
    if (!window.confirm(`Delete voice profile for ${name}?`)) return;
    await axios.delete(`${API}/voice-profiles/${id}`);
    fetchProfiles();
  };

  const generateSample = async (vp) => {
    setGeneratingSample(vp.id);
    try {
      const sampleText = vp.series === 'signed-sealed-betrayed'
        ? `Rain slicked the Harlem sidewalks. ${vp.character_name} moved through the city like a shadow.`
        : `The stamp hit the page with authority. ${vp.character_name} knew this was bigger than a signature.`;
      await axios.post(`${API}/voice-profiles/${vp.id}/generate-sample`, { text: sampleText });
      fetchProfiles();
    } catch (err) { alert('Sample generation failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setGeneratingSample(null); }
  };

  const playSample = (vp) => {
    if (playingSample === vp.id && sampleAudioRef.current) { sampleAudioRef.current.pause(); setPlayingSample(null); return; }
    if (sampleAudioRef.current) sampleAudioRef.current.pause();
    const audio = new Audio(`${BACKEND_URL}${vp.sample_audio_url}`);
    sampleAudioRef.current = audio;
    audio.play();
    setPlayingSample(vp.id);
    audio.onended = () => setPlayingSample(null);
  };

  return (
    <div className="mb-6 p-4 border border-white/10 rounded-lg bg-white/[0.02]" data-testid="voice-profiles-section">
      <div className="flex items-center justify-between mb-1">
        <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 group" data-testid="voice-collapse-toggle">
          {collapsed ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
          <Mic size={18} className="text-purple-500" />
          <h3 className="font-anton text-sm tracking-wider">CHARACTER VOICES</h3>
          <span className="text-xs text-gray-500 ml-1">{voiceProfiles.length} profiles</span>
        </button>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <a href={`${API}/export/character-voice-guide`} download className="px-2.5 py-1 text-[11px] font-bold bg-white/5 hover:bg-white/10 border border-white/10 rounded transition-all flex items-center gap-1.5" data-testid="download-voice-guide">
              <Download size={12} /> Guide
            </a>
            <button onClick={() => { setEditingId(null); setVoiceForm({ character_name: '', series: 'the-red-ink-chronicles', voice_type: 'ai', ai_voice: 'onyx', ai_speed: 1.0, description: '', is_narrator: false }); setShowForm(!showForm); }}
              className="px-2.5 py-1 text-[11px] font-bold bg-purple-600 hover:bg-purple-700 text-white rounded transition-all flex items-center gap-1.5"
              data-testid="add-voice-btn">
              <Plus size={12} /> New Voice
            </button>
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          {showForm && (
            <div className="mb-4 p-4 border border-purple-500/30 rounded-lg bg-purple-950/10" data-testid="voice-form">
              <h4 className="text-sm font-bold mb-3 text-purple-400">{editingId ? 'Edit Voice Profile' : 'New Voice Profile'}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input className="w-full p-2 text-sm bg-[#1a1a1a] border border-white/15 rounded focus:border-purple-500 outline-none text-white placeholder:text-gray-500"
                  placeholder="Character Name" value={voiceForm.character_name}
                  onChange={e => setVoiceForm(f => ({ ...f, character_name: e.target.value }))} data-testid="voice-name-input" />
                <select className="w-full p-2 text-sm bg-[#1a1a1a] border border-white/15 rounded focus:border-purple-500 outline-none text-white"
                  value={voiceForm.series} onChange={e => setVoiceForm(f => ({ ...f, series: e.target.value }))} data-testid="voice-series-select">
                  <option value="the-red-ink-chronicles">The Red Ink Chronicles</option>
                  <option value="the-notarys-shadow">The Notary's Shadow</option>
                  <option value="signed-sealed-betrayed">Signed, Sealed, Betrayed</option>
                </select>
              </div>
              <input className="w-full p-2 text-sm bg-[#1a1a1a] border border-white/15 rounded focus:border-purple-500 outline-none text-white placeholder:text-gray-500 mb-3"
                placeholder="Description (e.g., Deep noir protagonist voice, gruff and weathered)"
                value={voiceForm.description} onChange={e => setVoiceForm(f => ({ ...f, description: e.target.value }))} data-testid="voice-desc-input" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <select className="w-full p-2 text-sm bg-[#1a1a1a] border border-white/15 rounded focus:border-purple-500 outline-none text-white"
                  value={voiceForm.voice_type} onChange={e => setVoiceForm(f => ({ ...f, voice_type: e.target.value }))} data-testid="voice-type-select">
                  <option value="ai">AI Voice (TTS)</option>
                  <option value="uploaded">Uploaded Audio</option>
                </select>
                {voiceForm.voice_type === 'ai' && (
                  <>
                    <select className="w-full p-2 text-sm bg-[#1a1a1a] border border-white/15 rounded focus:border-purple-500 outline-none text-white"
                      value={voiceForm.ai_voice} onChange={e => setVoiceForm(f => ({ ...f, ai_voice: e.target.value }))} data-testid="voice-ai-voice-select">
                      <option value="alloy">Alloy (Sharp, Quick)</option>
                      <option value="echo">Echo (Deep, Measured)</option>
                      <option value="fable">Fable (Storyteller)</option>
                      <option value="onyx">Onyx (Deep, Authoritative)</option>
                      <option value="nova">Nova (Warm, Bright)</option>
                      <option value="shimmer">Shimmer (Bold, Dynamic)</option>
                    </select>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500 whitespace-nowrap">Speed</label>
                      <input type="range" min="0.5" max="1.5" step="0.05" className="flex-1" value={voiceForm.ai_speed}
                        onChange={e => setVoiceForm(f => ({ ...f, ai_speed: parseFloat(e.target.value) }))} data-testid="voice-speed-range" />
                      <span className="text-xs text-gray-400 w-8">{voiceForm.ai_speed.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={voiceForm.is_narrator} onChange={e => setVoiceForm(f => ({ ...f, is_narrator: e.target.checked }))}
                    className="accent-purple-500" data-testid="voice-narrator-check" />
                  <span className="text-gray-400 text-xs">Narrator</span>
                </label>
              </div>
              <div className="flex gap-2">
                <button onClick={saveProfile} className="px-4 py-2 text-sm font-bold bg-purple-600 hover:bg-purple-700 text-white rounded transition-all" data-testid="voice-save-btn">
                  {editingId ? 'Update' : 'Create'} Profile
                </button>
                <button onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-all">Cancel</button>
              </div>
            </div>
          )}

          {voiceProfiles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {voiceProfiles.map(vp => (
                <div key={vp.id} className={`p-3 border rounded-lg transition-all hover:border-purple-500/40 ${vp.is_narrator ? 'border-amber-500/30 bg-amber-950/10' : 'border-white/10 bg-white/[0.01]'}`} data-testid={`voice-profile-${vp.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold truncate">{vp.character_name}</h4>
                      <p className="text-[10px] text-gray-500">
                        {vp.series === 'signed-sealed-betrayed' ? 'SSB' : 'TRIC'}
                        {vp.is_narrator && <span className="text-amber-500 ml-1">NARRATOR</span>}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setEditingId(vp.id); setVoiceForm({ character_name: vp.character_name, series: vp.series, voice_type: vp.voice_type, ai_voice: vp.ai_voice || 'onyx', ai_speed: vp.ai_speed || 1.0, description: vp.description || '', is_narrator: vp.is_narrator }); setShowForm(true); }}
                        className="p-1 text-gray-500 hover:text-purple-400 transition-all" data-testid={`voice-edit-${vp.id}`}>
                        <FileText size={12} />
                      </button>
                      <button onClick={() => deleteProfile(vp.id, vp.character_name)}
                        className="p-1 text-gray-600 hover:text-red-500 transition-all" data-testid={`voice-delete-${vp.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  {vp.description && <p className="text-[11px] text-gray-500 mb-2 line-clamp-2">{vp.description}</p>}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${vp.voice_type === 'ai' ? 'bg-blue-600/30 text-blue-400' : 'bg-green-600/30 text-green-400'}`}>
                      {vp.voice_type === 'ai' ? `AI: ${vp.ai_voice || '—'}` : 'UPLOADED'}
                    </span>
                    {vp.voice_type === 'ai' && vp.ai_speed && vp.ai_speed !== 1.0 && <span className="text-[10px] text-gray-600">{vp.ai_speed}x</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {vp.sample_audio_url && (
                      <button onClick={() => playSample(vp)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-white/5 hover:bg-white/10 border border-white/10 rounded transition-all"
                        data-testid={`voice-play-sample-${vp.id}`}>
                        {playingSample === vp.id ? <><Pause size={10} /> Stop</> : <><Play size={10} /> Sample</>}
                      </button>
                    )}
                    {vp.voice_type === 'ai' && (
                      <button onClick={() => generateSample(vp)} disabled={generatingSample === vp.id}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-500/20 rounded transition-all"
                        data-testid={`voice-gen-sample-${vp.id}`}>
                        {generatingSample === vp.id ? <><Loader2 size={10} className="animate-spin" /> Gen...</> : <><Zap size={10} /> Gen Sample</>}
                      </button>
                    )}
                    {vp.voice_type === 'uploaded' && !vp.uploaded_audio_url && (
                      <label className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/20 rounded transition-all cursor-pointer">
                        <Upload size={10} /> Upload
                        <input type="file" accept="audio/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append('file', file);
                          try { await axios.post(`${API}/voice-profiles/${vp.id}/upload-sample`, fd); fetchProfiles(); }
                          catch (err) { alert('Upload failed'); }
                        }} />
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-600">
              <Mic size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No voice profiles yet</p>
              <p className="text-xs text-gray-700 mt-1">Create character voice profiles for audio production</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VoiceProfileManager;
